import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const InitialInfoScreen = ({ onSubmit, onBack, initialData = {} }) => {
  const [formData, setFormData] = useState({
    nombre_empresa: initialData.nombre_empresa || "",
    tipo_negocio: initialData.tipo_negocio || "",
    tipo_negocio_detalle: initialData.tipo_negocio_detalle || "",
    tamano_equipo: initialData.tamano_equipo || ""
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nombre_empresa.trim()) {
      newErrors.nombre_empresa = "El nombre de la empresa es obligatorio.";
    }
    if (!formData.tipo_negocio) {
      newErrors.tipo_negocio = "Por favor, selecciona el tipo de negocio.";
    }
    if (formData.tipo_negocio === "otro" && !formData.tipo_negocio_detalle.trim()) {
      newErrors.tipo_negocio_detalle = "Por favor, especifica el tipo de negocio.";
    }
    if (!formData.tamano_equipo) {
      newErrors.tamano_equipo = "Por favor, selecciona el tamaño del equipo.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const tiposNegocio = [
    { value: "agencia", label: "Agencia / Marketing" },
    { value: "consultoria", label: "Consultoría" },
    { value: "infoproductos", label: "Infoproductos / Formación" },
    { value: "otro", label: "Otro" }
  ];

  const tamanosEquipo = [
    { value: "independiente", label: "Profesional independiente" },
    { value: "2-5", label: "2–5 personas" },
    { value: "6-10", label: "6–10 personas" },
    { value: "11-25", label: "11–25 personas" },
    { value: "25+", label: "+25 personas" }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center pb-8 sm:pb-12 px-4 slide-up" style={{ paddingTop: '110px' }}>
      <div className="max-w-2xl w-full space-y-6 sm:space-y-8">
        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[hsl(var(--text-primary))] leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Cuéntanos sobre tu negocio
          </h2>
          <p className="text-sm sm:text-base text-[hsl(var(--text-secondary))]" style={{ letterSpacing: '-0.01em' }}>
            Necesitamos estos datos básicos para personalizar tu análisis.
          </p>
        </div>

        <div className="space-y-5 sm:space-y-6">
          {/* Nombre de la empresa */}
          <div className="space-y-2">
            <Label htmlFor="nombre_empresa" className="text-base font-semibold text-[hsl(var(--text-primary))]" style={{ letterSpacing: '-0.01em' }}>
              Nombre de la empresa
            </Label>
            <Input
              id="nombre_empresa"
              value={formData.nombre_empresa}
              onChange={(e) => handleChange("nombre_empresa", e.target.value)}
              placeholder="Ej: Mi Empresa SL"
              className="h-12 text-base"
            />
            {errors.nombre_empresa && (
              <p className="text-sm text-[hsl(var(--state-error))]">{errors.nombre_empresa}</p>
            )}
          </div>

          {/* Tipo de negocio */}
          <div className="space-y-2">
            <Label htmlFor="tipo_negocio" className="text-base font-semibold text-[hsl(var(--text-primary))]" style={{ letterSpacing: '-0.01em' }}>
              Tipo de negocio
            </Label>
            <Select
              value={formData.tipo_negocio}
              onValueChange={(value) => handleChange("tipo_negocio", value)}
            >
              <SelectTrigger className="h-12 text-base">
                <SelectValue placeholder="Selecciona el tipo de negocio" />
              </SelectTrigger>
              <SelectContent>
                {tiposNegocio.map((tipo) => (
                  <SelectItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.tipo_negocio && (
              <p className="text-sm text-[hsl(var(--state-error))]">{errors.tipo_negocio}</p>
            )}
          </div>

          {/* Campo condicional: Especifica tipo de negocio */}
          {formData.tipo_negocio === "otro" && (
            <div className="space-y-2 animate-slide-up">
              <Label htmlFor="tipo_negocio_detalle" className="text-base font-semibold text-[hsl(var(--text-primary))]" style={{ letterSpacing: '-0.01em' }}>
                Especifica qué tipo de negocio tienes
              </Label>
              <Input
                id="tipo_negocio_detalle"
                value={formData.tipo_negocio_detalle}
                onChange={(e) => handleChange("tipo_negocio_detalle", e.target.value)}
                placeholder="Ej: Ecommerce, SaaS, etc."
                className="h-12 text-base"
              />
              {errors.tipo_negocio_detalle && (
                <p className="text-sm text-[hsl(var(--state-error))]">{errors.tipo_negocio_detalle}</p>
              )}
            </div>
          )}

          {/* Tamaño del equipo */}
          <div className="space-y-2">
            <Label htmlFor="tamano_equipo" className="text-base font-semibold text-[hsl(var(--text-primary))]" style={{ letterSpacing: '-0.01em' }}>
              Tamaño del equipo
            </Label>
            <Select
              value={formData.tamano_equipo}
              onValueChange={(value) => handleChange("tamano_equipo", value)}
            >
              <SelectTrigger className="h-12 text-base">
                <SelectValue placeholder="Selecciona el tamaño del equipo" />
              </SelectTrigger>
              <SelectContent>
                {tamanosEquipo.map((tamano) => (
                  <SelectItem key={tamano.value} value={tamano.value}>
                    {tamano.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.tamano_equipo && (
              <p className="text-sm text-[hsl(var(--state-error))]">{errors.tamano_equipo}</p>
            )}
          </div>
        </div>

        <div className="pt-4">
          <div className="flex items-center gap-4">
            {onBack && (
              <Button
                onClick={onBack}
                variant="secondary"
                size="lg"
                className="h-14 px-6 text-base font-semibold"
              >
                Atrás
              </Button>
            )}
            
            <Button
              onClick={handleSubmit}
              size="lg"
              className="flex-1 h-14 text-base font-semibold"
            >
              Continuar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InitialInfoScreen;
